import { useMemo, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import {
  useAvailableAgents,
  useBindings,
  useCoverageMap,
  useAgentMutations,
  useCompanyLlmConfigs,
  useCompanyLlmMutations,
  useCompanyVoiceConfigs,
  useCompanyVoiceMutations,
  useReferenceableAgents,
  useVoices,
  fetchVoicePreviewUrl,
  type CompanyLlmConfig,
  type CompanyVoiceConfig,
  type AgentBinding,
  type AvailableAgent,
  type CoverageRow,
} from "../../hooks/useAgents";
import { useMe } from "../../hooks/useMe";

type Tab = "fleet" | "coverage" | "telemetry";

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: "fleet", label: "Fleet" },
  { key: "coverage", label: "Capability coverage" },
  { key: "telemetry", label: "Telemetry" },
];

function roleForWorkspace(
  me:
    | {
        memberships?: Array<{ workspaceId: string; role: string }>;
      }
    | undefined,
  workspaceId: string,
): string | null {
  if (!me?.memberships) return null;
  const m = me.memberships.find((x) => x.workspaceId === workspaceId);
  return m?.role ?? null;
}

function canManage(role: string | null): boolean {
  return role === "cxo" || role === "agent_handler";
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString();
}

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) {
    return (
      <span style={{ color: "var(--text-3)", fontSize: 11 }}>no data</span>
    );
  }
  const w = 90;
  const h = 22;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map(
      (v, i) =>
        `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`,
    )
    .join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface AgentCardProps {
  binding: AgentBinding;
  readOnly: boolean;
  onPause: (id: string) => void;
  onTest: (id: string) => void;
  onRevoke: (id: string) => void;
  testResult?: { ok: boolean; error?: string } | null;
}

function readSparkData(t: Record<string, unknown>): number[] {
  const v = t["sparkline"];
  if (Array.isArray(v) && v.every((x) => typeof x === "number")) {
    return v as number[];
  }
  return [];
}

function AgentCard({
  binding,
  readOnly,
  onPause,
  onTest,
  onRevoke,
  testResult,
}: AgentCardProps) {
  const t = binding.telemetry ?? {};
  const inv24 = typeof t["invocations_24h"] === "number"
    ? (t["invocations_24h"] as number)
    : 0;
  const success = typeof t["success_pct"] === "number"
    ? (t["success_pct"] as number)
    : null;
  const p50 = typeof t["p50_ms"] === "number" ? `${t["p50_ms"]}ms` : "—";
  const cost = typeof t["cost_today_usd"] === "number"
    ? `$${(t["cost_today_usd"] as number).toFixed(2)}`
    : "—";
  const spark = readSparkData(t);
  const expiringSoon =
    binding.expires_at !== null &&
    new Date(binding.expires_at).getTime() - Date.now() < 7 * 86_400_000;
  const healthLabel = !binding.enabled
    ? "paused"
    : expiringSoon
      ? "expiring"
      : "healthy";
  const healthColor = !binding.enabled
    ? "var(--text-mute)"
    : expiringSoon
      ? "var(--warn)"
      : "var(--accent)";

  return (
    <div className="agent-card" data-testid={`agent-card-${binding.id}`}>
      <div className="agent-card-head">
        <div>
          <div className="agent-name">{binding.agent_name}</div>
          <div className="agent-vendor">{binding.surface}</div>
        </div>
        <span className="pill gray">{binding.surface}</span>
      </div>
      <div className="agent-health-row">
        <span className="health-pill">
          <span className="d" style={{ background: healthColor }} />
          {healthLabel}
        </span>
        <div className="agent-spark">
          <MiniSparkline data={spark} color={healthColor} />
        </div>
      </div>
      <div className="agent-stats">
        <div>
          <div className="agent-stat-l">24H</div>
          <div className="agent-stat-v">{inv24.toLocaleString()}</div>
        </div>
        <div>
          <div className="agent-stat-l">SUCCESS</div>
          <div
            className={`agent-stat-v ${success !== null && success < 90 ? "red" : ""}`}
          >
            {success !== null ? `${success}%` : "—"}
          </div>
        </div>
        <div>
          <div className="agent-stat-l">P50</div>
          <div className="agent-stat-v">{p50}</div>
        </div>
        <div>
          <div className="agent-stat-l">COST</div>
          <div className="agent-stat-v">{cost}</div>
        </div>
      </div>
      <div className="agent-plays">
        <div className="agent-plays-l">BOUND BY</div>
        <div className="agent-plays-v">
          {binding.bound_by_user.display_name} ·{" "}
          {relativeTime(binding.bound_at)}
        </div>
      </div>
      {testResult && (
        <div
          className="agent-plays"
          data-testid={`test-result-${binding.id}`}
          style={{
            color: testResult.ok ? "var(--accent)" : "var(--danger)",
            fontSize: 12,
            marginTop: 4,
          }}
        >
          {testResult.ok ? "Test OK" : `Test failed: ${testResult.error ?? "unknown"}`}
        </div>
      )}
      {!readOnly && (
        <div className="agent-foot">
          <span className="agent-mandate">
            {binding.enabled ? "Active" : "Paused"}
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="agent-act"
            onClick={() => onPause(binding.id)}
            data-testid={`pause-${binding.id}`}
          >
            {expiringSoon ? "Reconnect" : binding.enabled ? "Pause" : "Resume"}
          </button>
          <button
            type="button"
            className="agent-act subtle"
            onClick={() => onTest(binding.id)}
            data-testid={`test-${binding.id}`}
          >
            Test
          </button>
          <button
            type="button"
            className="agent-act subtle"
            onClick={() => onRevoke(binding.id)}
            data-testid={`revoke-${binding.id}`}
          >
            Revoke
          </button>
        </div>
      )}
    </div>
  );
}

interface BindModalProps {
  agents: AvailableAgent[];
  onClose: () => void;
  onSubmit: (body: {
    agent_definition_name: string;
    credentials: Record<string, unknown>;
    scope: Record<string, unknown>;
  }) => void;
  busy: boolean;
  error: string | null;
}

function BindModal({ agents, onClose, onSubmit, busy, error }: BindModalProps) {
  const [agentName, setAgentName] = useState<string>(agents[0]?.name ?? "");
  const [endpoint, setEndpoint] = useState("");
  const [secret, setSecret] = useState("");
  const [token, setToken] = useState("");

  const selected = agents.find((a) => a.name === agentName);
  const surface = selected?.surface ?? "custom_webhook";

  const submit = () => {
    if (!agentName) return;
    let credentials: Record<string, unknown> = {};
    if (surface === "custom_webhook") {
      credentials = { endpoint, secret };
    } else if (surface === "cowork" || surface === "mcp_generic") {
      credentials = { mcp_endpoint: endpoint, token };
    } else if (surface === "copilot") {
      credentials = {};
    }
    onSubmit({ agent_definition_name: agentName, credentials, scope: {} });
  };

  return (
    <div
      className="tpl-drawer-bg"
      onClick={onClose}
      data-testid="bind-modal"
    >
      <div
        className="tpl-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <div className="tpl-drw-h">
          <div>
            <div className="tpl-arch">BIND AGENT</div>
            <div className="tpl-drw-t">Connect a new agent</div>
          </div>
          <button type="button" className="tpl-drw-x" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="tpl-drw-section">
          <div className="tpl-drw-lbl">Agent</div>
          <select
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            data-testid="bind-agent-select"
            className="tpl-fpill"
            style={{ width: "100%", padding: "6px 8px" }}
          >
            {agents.map((a) => (
              <option key={a.id} value={a.name} disabled={a.is_bound_in_workspace}>
                {a.name} ({a.surface})
                {a.is_bound_in_workspace ? " — already bound" : ""}
              </option>
            ))}
          </select>
        </div>

        {surface === "custom_webhook" && (
          <div className="tpl-drw-section">
            <div className="tpl-drw-lbl">Webhook endpoint</div>
            <input
              type="url"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="https://your-agent.example.com/invoke"
              data-testid="bind-endpoint"
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                marginBottom: 10,
              }}
            />
            <div className="tpl-drw-lbl">HMAC secret</div>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              data-testid="bind-secret"
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
              }}
            />
          </div>
        )}

        {(surface === "cowork" || surface === "mcp_generic") && (
          <div className="tpl-drw-section">
            <div className="tpl-drw-lbl">MCP endpoint</div>
            <input
              type="url"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="wss://mcp.example.com"
              data-testid="bind-endpoint"
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                marginBottom: 10,
              }}
            />
            <div className="tpl-drw-lbl">Bearer token</div>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="bind-token"
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
              }}
            />
          </div>
        )}

        {surface === "copilot" && (
          <div className="tpl-drw-section">
            <div
              style={{
                fontSize: 12,
                color: "var(--text-3)",
                lineHeight: 1.5,
              }}
            >
              You will be redirected to Microsoft to grant consent.
            </div>
          </div>
        )}

        {surface === "nango_connector" && (
          <div className="tpl-drw-section">
            <div
              style={{
                fontSize: 12,
                color: "var(--text-3)",
                lineHeight: 1.5,
              }}
            >
              You will be redirected to the provider to grant access. Once
              authorized, your connection will be saved automatically.
            </div>
          </div>
        )}

        {error && (
          <div
            className="tpl-drw-section"
            data-testid="bind-error"
            style={{ color: "var(--danger)", fontSize: 12 }}
          >
            {error}
          </div>
        )}

        <div className="tpl-drw-f">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={submit}
            disabled={busy}
            data-testid="bind-submit"
          >
            {busy ? "Binding…" : "Bind agent →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CoverageMatrix({
  rows,
  bindings,
}: {
  rows: CoverageRow[];
  bindings: AgentBinding[];
}) {
  return (
    <div className="coverage-matrix" data-testid="coverage-matrix">
      <div className="cov-head">
        <div className="cov-cap">CAPABILITY</div>
        {bindings.map((b) => (
          <div key={b.id} className="cov-agent" title={b.agent_name}>
            {b.agent_name
              .split(/[\s_-]+/)
              .slice(0, 2)
              .map((s) => s[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
        ))}
      </div>
      {rows.map((row) => {
        const gap = row.covered_by.length === 0;
        const covered = new Set(row.covered_by.map((c) => c.binding_id));
        return (
          <div
            key={row.id}
            className={`cov-row ${gap ? "gap" : ""}`}
            data-testid={`cov-row-${row.id}`}
            data-gap={gap ? "true" : "false"}
          >
            <div className="cov-cap">
              <span>{row.namespace}</span>
              {gap && <span className="cov-gap-cta">+ Bind agent</span>}
            </div>
            {bindings.map((b) => (
              <div key={b.id} className="cov-cell">
                {covered.has(b.id) && <span className="cov-dot" />}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface ByoModelPanelProps {
  workspaceId: string;
  manage: boolean;
}

const PROVIDER_LABELS: Record<CompanyLlmConfig["provider"], string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  gemini: "Google Gemini",
  azure_openai: "Azure OpenAI",
};

function ByoModelPanel({ workspaceId, manage }: ByoModelPanelProps) {
  const cfgQ = useCompanyLlmConfigs(workspaceId);
  const { set, promoteDefault, test, remove } = useCompanyLlmMutations(workspaceId);
  const [adding, setAdding] = useState<CompanyLlmConfig["provider"] | null>(null);
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [testResult, setTestResult] = useState<
    Record<string, { ok: boolean; detail?: string } | undefined>
  >({});

  const items = cfgQ.data?.items ?? [];
  const hasAny = items.length > 0;
  const hasDefault = items.some((c) => c.isDefault);

  function resetAdd() {
    setAdding(null);
    setModel("");
    setApiKey("");
  }

  return (
    <div
      className="agent-card"
      style={{ marginTop: 24 }}
      data-testid="byo-model-panel"
    >
      <div className="agent-card-head">
        <div>
          <div className="agent-name">LLM Provider Keys</div>
          <div className="agent-vendor">
            {hasAny
              ? `${items.length} provider${items.length === 1 ? "" : "s"} configured at the company level`
              : "Configure at least one LLM key to enable agent runs"}
          </div>
        </div>
        {hasDefault ? (
          <span className="pill green" data-testid="byo-status">active</span>
        ) : (
          <span className="pill gray" data-testid="byo-status">inactive — no key</span>
        )}
      </div>

      {!hasDefault && (
        <div
          data-testid="byo-inactive-banner"
          style={{
            marginTop: 12,
            padding: "10px 12px",
            background: "color-mix(in srgb, var(--danger, #d9534f) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--danger, #d9534f) 30%, transparent)",
            borderRadius: 6,
            fontSize: 12,
            color: "var(--text-2)",
          }}
        >
          No LLM key configured for this company. Hand tests, eval runs, and
          prompt improvement are disabled until a provider is added below.
        </div>
      )}

      {items.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((cfg) => {
            const r = testResult[cfg.provider];
            return (
              <div
                key={cfg.provider}
                data-testid={`byo-row-${cfg.provider}`}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: cfg.isDefault
                    ? "color-mix(in srgb, var(--accent, #6366f1) 6%, transparent)"
                    : "var(--bg-soft)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong style={{ fontSize: 13 }}>
                    {PROVIDER_LABELS[cfg.provider]}
                  </strong>
                  {typeof cfg.config["model"] === "string" && (
                    <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                      · {String(cfg.config["model"])}
                    </span>
                  )}
                  {cfg.isDefault && (
                    <span className="pill green" style={{ fontSize: 10 }}>
                      default
                    </span>
                  )}
                  {manage && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      {!cfg.isDefault && (
                        <button
                          type="button"
                          className="agent-act subtle"
                          data-testid={`byo-default-${cfg.provider}`}
                          onClick={() => promoteDefault.mutate(cfg.provider)}
                          disabled={promoteDefault.isPending}
                        >
                          Set default
                        </button>
                      )}
                      <button
                        type="button"
                        className="agent-act subtle"
                        data-testid={`byo-test-${cfg.provider}`}
                        onClick={() =>
                          test.mutate(cfg.provider, {
                            onSuccess: (out) =>
                              setTestResult((s) => ({ ...s, [cfg.provider]: out })),
                          })
                        }
                        disabled={test.isPending}
                      >
                        Test
                      </button>
                      <button
                        type="button"
                        className="agent-act subtle"
                        data-testid={`byo-remove-${cfg.provider}`}
                        onClick={() => remove.mutate(cfg.provider)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                {r && (
                  <div
                    data-testid={`byo-test-result-${cfg.provider}`}
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: r.ok ? "var(--accent)" : "var(--danger)",
                    }}
                  >
                    {r.ok ? "Connection OK" : `Failed: ${r.detail ?? "unknown"}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {manage && (
        adding ? (
          <div
            style={{ marginTop: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}
          >
            <div className="tpl-drw-lbl">Adding {PROVIDER_LABELS[adding]} key</div>
            <div className="tpl-drw-lbl" style={{ marginTop: 8 }}>Model (optional)</div>
            <input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              data-testid="byo-model"
              placeholder={
                adding === "anthropic" ? "claude-opus-4-7"
                  : adding === "openai" ? "gpt-4o"
                  : adding === "gemini" ? "gemini-2.5-flash"
                  : "deployment-name"
              }
              style={{
                width: "100%", padding: "6px 8px",
                border: "1px solid var(--border)", borderRadius: 6, marginBottom: 8,
              }}
            />
            <div className="tpl-drw-lbl">API key</div>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="byo-key"
              style={{
                width: "100%", padding: "6px 8px",
                border: "1px solid var(--border)", borderRadius: 6, marginBottom: 8,
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn primary"
                data-testid="byo-save"
                onClick={() =>
                  set.mutate(
                    {
                      provider: adding,
                      body: {
                        credentials: { api_key: apiKey },
                        ...(model ? { config: { model } } : {}),
                      },
                    },
                    { onSuccess: resetAdd },
                  )
                }
                disabled={set.isPending || !apiKey}
              >
                {set.isPending ? "Saving…" : "Save key"}
              </button>
              <button type="button" className="btn" onClick={resetAdd}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="agent-foot" style={{ marginTop: 12 }}>
            {(["anthropic", "openai", "gemini", "azure_openai"] as const)
              .filter((p) => !items.some((c) => c.provider === p))
              .map((p) => (
                <button
                  key={p}
                  type="button"
                  className="agent-act"
                  data-testid={`byo-add-${p}`}
                  onClick={() => setAdding(p)}
                >
                  + Add {PROVIDER_LABELS[p]}
                </button>
              ))}
          </div>
        )
      )}
    </div>
  );
}

interface VoiceConfigPanelProps {
  workspaceId: string;
  manage: boolean;
}

/**
 * Company-scoped ElevenLabs BYOK. PR1 surfaces the API key + a default voice
 * (drives TTS). SIP trunking and WhatsApp are shown as scaffolded-but-inactive
 * so the customer sees what's coming; they activate in the telephony PR.
 */
function VoiceConfigPanel({ workspaceId, manage }: VoiceConfigPanelProps) {
  const cfgQ = useCompanyVoiceConfigs(workspaceId);
  const { set, test, remove } = useCompanyVoiceMutations(workspaceId);
  const [adding, setAdding] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [testResult, setTestResult] = useState<
    { ok: boolean; detail?: string } | undefined
  >();

  const items = cfgQ.data?.items ?? [];
  const cfg: CompanyVoiceConfig | undefined = items.find(
    (c) => c.provider === "elevenlabs",
  );
  const active = !!cfg;

  function resetAdd() {
    setAdding(false);
    setApiKey("");
    setVoiceId("");
  }

  return (
    <div
      className="agent-card"
      style={{ marginTop: 24 }}
      data-testid="voice-config-panel"
    >
      <div className="agent-card-head">
        <div>
          <div className="agent-name">Voice &amp; Conversational AI (ElevenLabs)</div>
          <div className="agent-vendor">
            {active
              ? "ElevenLabs key configured — text-to-speech enabled"
              : "Add your ElevenLabs API key to enable voice agents"}
          </div>
        </div>
        {active ? (
          <span className="pill green" data-testid="voice-status">active</span>
        ) : (
          <span className="pill gray" data-testid="voice-status">inactive — no key</span>
        )}
      </div>

      {active && cfg && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            data-testid="voice-row-elevenlabs"
            style={{
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "10px 12px",
              background: "color-mix(in srgb, var(--accent, #6366f1) 6%, transparent)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 13 }}>ElevenLabs</strong>
              {typeof cfg.config.default_voice_id === "string" && (
                <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                  · voice {String(cfg.config.default_voice_id)}
                </span>
              )}
              {manage && (
                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    className="agent-act subtle"
                    data-testid="voice-test"
                    onClick={() =>
                      test.mutate("elevenlabs", {
                        onSuccess: (out) => setTestResult(out),
                      })
                    }
                    disabled={test.isPending}
                  >
                    Test
                  </button>
                  <button
                    type="button"
                    className="agent-act subtle"
                    data-testid="voice-remove"
                    onClick={() => remove.mutate("elevenlabs")}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            {testResult && (
              <div
                data-testid="voice-test-result"
                style={{
                  marginTop: 6,
                  fontSize: 11,
                  color: testResult.ok ? "var(--accent)" : "var(--danger)",
                }}
              >
                {testResult.ok
                  ? "Connection OK"
                  : `Failed: ${testResult.detail ?? "unknown"}`}
              </div>
            )}
          </div>
        </div>
      )}

      {active && cfg && manage && (
        <VoiceDefaultPicker workspaceId={workspaceId} cfg={cfg} />
      )}

      {active && cfg && (
        <VoiceTelephonyPanel
          workspaceId={workspaceId}
          cfg={cfg}
          manage={manage}
        />
      )}

      {manage && (
        adding ? (
          <div
            style={{ marginTop: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}
          >
            <div className="tpl-drw-lbl">Adding ElevenLabs key</div>
            <div className="tpl-drw-lbl" style={{ marginTop: 8 }}>API key</div>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="voice-key"
              style={{
                width: "100%", padding: "6px 8px",
                border: "1px solid var(--border)", borderRadius: 6, marginBottom: 8,
              }}
            />
            <div className="tpl-drw-lbl">Default voice ID (optional)</div>
            <input
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              data-testid="voice-default-voice"
              placeholder="21m00Tcm4TlvDq8ikWAM"
              style={{
                width: "100%", padding: "6px 8px",
                border: "1px solid var(--border)", borderRadius: 6, marginBottom: 8,
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn primary"
                data-testid="voice-save"
                onClick={() =>
                  set.mutate(
                    {
                      provider: "elevenlabs",
                      body: {
                        credentials: { api_key: apiKey },
                        ...(voiceId ? { config: { default_voice_id: voiceId } } : {}),
                      },
                    },
                    { onSuccess: resetAdd },
                  )
                }
                disabled={set.isPending || !apiKey}
              >
                {set.isPending ? "Saving…" : "Save key"}
              </button>
              <button type="button" className="btn" onClick={resetAdd}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          !active && (
            <div className="agent-foot" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="agent-act"
                data-testid="voice-add-elevenlabs"
                onClick={() => setAdding(true)}
              >
                + Add ElevenLabs key
              </button>
            </div>
          )
        )
      )}
    </div>
  );
}

interface VoiceDefaultPickerProps {
  workspaceId: string;
  cfg: CompanyVoiceConfig;
}

/**
 * Default-voice picker for an active ElevenLabs config: a dropdown populated
 * from GET /voices plus a Preview button that streams a short TTS clip and
 * plays it. Saves via the config-patch endpoint (PUT /agent).
 */
function VoiceDefaultPicker({ workspaceId, cfg }: VoiceDefaultPickerProps) {
  const voicesQ = useVoices(workspaceId, true);
  const { setAgent } = useCompanyVoiceMutations(workspaceId);
  const current =
    typeof cfg.config.default_voice_id === "string" ? cfg.config.default_voice_id : "";
  const [sel, setSel] = useState(current);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  async function preview() {
    const voiceId = sel || current;
    if (!voiceId) return;
    setPreviewErr(null);
    setPreviewing(true);
    let url: string | null = null;
    try {
      url = await fetchVoicePreviewUrl(workspaceId, {
        text: "Hi — this is a preview of your selected ElevenLabs voice.",
        voice_id: voiceId,
      });
      const audio = new Audio(url);
      audio.onended = () => {
        if (url) URL.revokeObjectURL(url);
      };
      await audio.play();
    } catch (e) {
      if (url) URL.revokeObjectURL(url);
      setPreviewErr(e instanceof Error ? e.message : "preview failed");
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <div
      data-testid="voice-default-picker"
      style={{ marginTop: 12, border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}
    >
      <div className="tpl-drw-lbl">Default voice</div>
      <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>
        Used for text-to-speech when a step doesn't specify one.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <select
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          data-testid="voice-default-select"
          style={{ ...fieldStyle, marginBottom: 0 }}
        >
          <option value="">
            {voicesQ.isLoading
              ? "Loading voices…"
              : voicesQ.isError
                ? "Couldn't load voices"
                : "Select a voice"}
          </option>
          {(voicesQ.data?.voices ?? []).map((v) => (
            <option key={v.voice_id} value={v.voice_id}>
              {v.name}
              {v.category ? ` · ${v.category}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="agent-act subtle"
          data-testid="voice-default-preview"
          disabled={previewing || !(sel || current)}
          onClick={preview}
        >
          {previewing ? "Playing…" : "Preview"}
        </button>
        <button
          type="button"
          className="agent-act"
          data-testid="voice-default-save"
          disabled={!sel || sel === current || setAgent.isPending}
          onClick={() => setAgent.mutate({ default_voice_id: sel })}
        >
          Save
        </button>
      </div>
      {current && (
        <div style={{ fontSize: 11, color: "var(--accent)", marginTop: 6 }}>
          Current: {current}
        </div>
      )}
      {previewErr && (
        <div data-testid="voice-default-preview-err" style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>
          Preview failed: {previewErr}
        </div>
      )}
    </div>
  );
}

interface VoiceTelephonyPanelProps {
  workspaceId: string;
  cfg: CompanyVoiceConfig;
  manage: boolean;
}

const fieldStyle: CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid var(--border)",
  borderRadius: 6,
  marginBottom: 8,
};

/**
 * Telephony activation for an already-keyed ElevenLabs config: reference an
 * existing agent, import a SIP trunk number, link a WhatsApp channel, and place
 * a test outbound call. Visible only once the API key is active.
 */
function VoiceTelephonyPanel({ workspaceId, cfg, manage }: VoiceTelephonyPanelProps) {
  const { configureSip, setAgent, outboundCall } = useCompanyVoiceMutations(workspaceId);
  const agentsQ = useReferenceableAgents(workspaceId, manage);

  const sip = (cfg.config.sip ?? {}) as Record<string, unknown>;
  const sipConfigured = typeof sip["phone_number_id"] === "string";
  const agentId =
    typeof cfg.config.default_agent_id === "string" ? cfg.config.default_agent_id : "";
  const wa = (cfg.config.whatsapp ?? {}) as Record<string, unknown>;

  const [agentSel, setAgentSel] = useState(agentId);
  const [sipForm, setSipForm] = useState({
    phone_number: "",
    label: "",
    address: "",
    transport: "auto" as "auto" | "udp" | "tcp" | "tls",
    media_encryption: "allowed" as "disabled" | "allowed" | "required",
    username: "",
    password: "",
  });
  const [waForm, setWaForm] = useState({
    phone_number_id: typeof wa["phone_number_id"] === "string" ? (wa["phone_number_id"] as string) : "",
    agent_id: typeof wa["agent_id"] === "string" ? (wa["agent_id"] as string) : "",
  });
  const [callTo, setCallTo] = useState("");
  const [callMsg, setCallMsg] = useState<string | null>(null);

  return (
    <div
      data-testid="voice-telephony-panel"
      style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div
        style={{
          padding: "8px 12px",
          background: "var(--bg-soft)",
          border: "1px dashed var(--border)",
          borderRadius: 6,
          fontSize: 11,
          color: "var(--text-3)",
        }}
      >
        Telephony (SIP + outbound calling) requires a paid ElevenLabs plan on
        your key.
      </div>

      {/* Referenced agent */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
        <div className="tpl-drw-lbl">Referenced agent</div>
        <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>
          Pick an agent you built in the ElevenLabs dashboard — it handles the calls.
        </div>
        {manage && (
          <div style={{ display: "flex", gap: 8 }}>
            <select
              value={agentSel}
              onChange={(e) => setAgentSel(e.target.value)}
              data-testid="voice-agent-select"
              style={{ ...fieldStyle, marginBottom: 0 }}
            >
              <option value="">
                {agentsQ.isLoading ? "Loading agents…" : "Select an agent"}
              </option>
              {(agentsQ.data?.agents ?? []).map((a) => (
                <option key={a.agent_id} value={a.agent_id}>
                  {a.name} ({a.agent_id.slice(0, 10)}…)
                </option>
              ))}
            </select>
            <button
              type="button"
              className="agent-act"
              data-testid="voice-agent-save"
              disabled={!agentSel || setAgent.isPending}
              onClick={() => setAgent.mutate({ default_agent_id: agentSel })}
            >
              Save
            </button>
          </div>
        )}
        {agentId && (
          <div style={{ fontSize: 11, color: "var(--accent)", marginTop: 6 }}>
            Current: {agentId}
          </div>
        )}
      </div>

      {/* SIP trunk */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="tpl-drw-lbl">SIP trunk</div>
          {sipConfigured ? (
            <span className="pill green" style={{ fontSize: 10 }} data-testid="voice-sip-status">
              number imported
            </span>
          ) : (
            <span className="pill gray" style={{ fontSize: 10 }} data-testid="voice-sip-status">
              not configured
            </span>
          )}
        </div>
        {sipConfigured && (
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
            {String(sip["phone_number"] ?? "")} · {String(sip["transport"] ?? "auto")} ·{" "}
            {String(sip["auth_type"] ?? "")}
          </div>
        )}
        {manage && (
          <div style={{ marginTop: 8 }}>
            <input
              placeholder="Phone number (E.164, e.g. +15551234567)"
              value={sipForm.phone_number}
              onChange={(e) => setSipForm((s) => ({ ...s, phone_number: e.target.value }))}
              data-testid="voice-sip-number"
              style={fieldStyle}
            />
            <input
              placeholder="Label"
              value={sipForm.label}
              onChange={(e) => setSipForm((s) => ({ ...s, label: e.target.value }))}
              data-testid="voice-sip-label"
              style={fieldStyle}
            />
            <input
              placeholder="Address (hostname/IP, no sip: prefix)"
              value={sipForm.address}
              onChange={(e) => setSipForm((s) => ({ ...s, address: e.target.value }))}
              data-testid="voice-sip-address"
              style={fieldStyle}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={sipForm.transport}
                onChange={(e) =>
                  setSipForm((s) => ({ ...s, transport: e.target.value as typeof s.transport }))
                }
                style={fieldStyle}
              >
                {(["auto", "udp", "tcp", "tls"] as const).map((t) => (
                  <option key={t} value={t}>transport: {t}</option>
                ))}
              </select>
              <select
                value={sipForm.media_encryption}
                onChange={(e) =>
                  setSipForm((s) => ({ ...s, media_encryption: e.target.value as typeof s.media_encryption }))
                }
                style={fieldStyle}
              >
                {(["disabled", "allowed", "required"] as const).map((m) => (
                  <option key={m} value={m}>media: {m}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                placeholder="SIP username (digest auth, optional)"
                value={sipForm.username}
                onChange={(e) => setSipForm((s) => ({ ...s, username: e.target.value }))}
                style={fieldStyle}
              />
              <input
                type="password"
                placeholder="SIP password"
                value={sipForm.password}
                onChange={(e) => setSipForm((s) => ({ ...s, password: e.target.value }))}
                style={fieldStyle}
              />
            </div>
            <button
              type="button"
              className="agent-act"
              data-testid="voice-sip-save"
              disabled={
                configureSip.isPending ||
                !sipForm.phone_number ||
                !sipForm.label ||
                !sipForm.address
              }
              onClick={() =>
                configureSip.mutate({
                  phone_number: sipForm.phone_number,
                  label: sipForm.label,
                  address: sipForm.address,
                  transport: sipForm.transport,
                  media_encryption: sipForm.media_encryption,
                  ...(sipForm.username ? { username: sipForm.username } : {}),
                  ...(sipForm.password ? { password: sipForm.password } : {}),
                })
              }
            >
              {configureSip.isPending ? "Importing…" : sipConfigured ? "Re-import number" : "Import SIP number"}
            </button>
            {configureSip.isError && (
              <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>
                {configureSip.error.message}
              </div>
            )}
          </div>
        )}
      </div>

      {/* WhatsApp (referenced channel) */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
        <div className="tpl-drw-lbl">WhatsApp (referenced)</div>
        <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>
          Link the agent's WhatsApp channel configured in ElevenLabs.
        </div>
        {manage && (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              placeholder="WhatsApp phone_number_id"
              value={waForm.phone_number_id}
              onChange={(e) => setWaForm((s) => ({ ...s, phone_number_id: e.target.value }))}
              data-testid="voice-wa-number"
              style={{ ...fieldStyle, marginBottom: 0 }}
            />
            <button
              type="button"
              className="agent-act"
              data-testid="voice-wa-save"
              disabled={setAgent.isPending}
              onClick={() =>
                setAgent.mutate({
                  whatsapp: {
                    ...(waForm.phone_number_id ? { phone_number_id: waForm.phone_number_id } : {}),
                    ...(waForm.agent_id ? { agent_id: waForm.agent_id } : {}),
                  },
                })
              }
            >
              Save
            </button>
          </div>
        )}
      </div>

      {/* Place a test call */}
      {manage && sipConfigured && agentId && (
        <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
          <div className="tpl-drw-lbl">Place a test call</div>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <input
              placeholder="To number (E.164)"
              value={callTo}
              onChange={(e) => setCallTo(e.target.value)}
              data-testid="voice-call-to"
              style={{ ...fieldStyle, marginBottom: 0 }}
            />
            <button
              type="button"
              className="btn primary"
              data-testid="voice-call-go"
              disabled={!callTo || outboundCall.isPending}
              onClick={() => {
                setCallMsg(null);
                outboundCall.mutate(
                  { to_number: callTo },
                  {
                    onSuccess: () => setCallMsg("Call placed ✓"),
                    onError: (e) => setCallMsg(`Failed: ${e.message}`),
                  },
                );
              }}
            >
              {outboundCall.isPending ? "Calling…" : "Call"}
            </button>
          </div>
          {callMsg && (
            <div
              data-testid="voice-call-result"
              style={{
                fontSize: 11,
                marginTop: 6,
                color: callMsg.startsWith("Failed") ? "var(--danger)" : "var(--accent)",
              }}
            >
              {callMsg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AgentManagement() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const meQ = useMe();
  const role = roleForWorkspace(meQ.data, workspaceId);
  const manage = canManage(role);

  const [tab, setTab] = useState<Tab>("fleet");
  const [bindOpen, setBindOpen] = useState(false);
  const [testResults, setTestResults] = useState<
    Record<string, { ok: boolean; error?: string }>
  >({});

  const availableQ = useAvailableAgents(workspaceId);
  const bindingsQ = useBindings(workspaceId);
  const coverageQ = useCoverageMap(workspaceId);
  const { bind, revoke, testConnection } = useAgentMutations(workspaceId);

  const bindings = bindingsQ.data?.bindings ?? [];
  const summary = useMemo(() => {
    const total = bindings.length;
    const enabled = bindings.filter((b) => b.enabled).length;
    const expiring = bindings.filter(
      (b) =>
        b.expires_at !== null &&
        new Date(b.expires_at).getTime() - Date.now() < 7 * 86_400_000,
    ).length;
    const inv24 = bindings.reduce((acc, b) => {
      const v = b.telemetry?.["invocations_24h"];
      return acc + (typeof v === "number" ? v : 0);
    }, 0);
    return { total, enabled, expiring, inv24 };
  }, [bindings]);

  const cov = coverageQ.data?.summary;

  const handleBindSubmit = (body: {
    agent_definition_name: string;
    credentials: Record<string, unknown>;
    scope: Record<string, unknown>;
  }) => {
    bind.mutate(body, {
      onSuccess: (r) => {
        if (r.next_step === "oauth_redirect" && r.authorize_url) {
          window.location.assign(r.authorize_url);
          return;
        }
        setBindOpen(false);
      },
    });
  };

  const handleTest = (bindingId: string) => {
    testConnection.mutate(bindingId, {
      onSuccess: (r) =>
        setTestResults((prev) => ({ ...prev, [bindingId]: r })),
      onError: (e) =>
        setTestResults((prev) => ({
          ...prev,
          [bindingId]: { ok: false, error: e.message },
        })),
    });
  };

  return (
    <div className="main-inner agents-surface" data-testid="agent-management">
      <div className="agents-h">
        <div>
          <div className="section-eyebrow">WORKSPACE</div>
          <div className="agents-title">Agents</div>
          <div className="agents-sub">
            {manage
              ? "Bind, scope, revoke, and monitor the fleet of agents your workspace consumes."
              : "Read-only fleet view. Your CXO or agent handler manages bindings."}
          </div>
        </div>
        <div className="agents-h-right">
          {manage ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setBindOpen(true)}
              data-testid="bind-cta"
            >
              + Bind agent
            </button>
          ) : (
            <span className="pill gray">READ-ONLY</span>
          )}
        </div>
      </div>

      <div className="agents-kpi">
        <div className="agents-kpi-card">
          <div className="agents-kpi-l">BOUND</div>
          <div className="agents-kpi-v" data-testid="kpi-bound">
            {summary.total}
          </div>
          <div className="agents-kpi-s">
            <span className="d ok" /> {summary.enabled} enabled
            {summary.expiring > 0 && (
              <>
                <span
                  className="d warn"
                  style={{ marginLeft: 8 }}
                />{" "}
                {summary.expiring} expiring
              </>
            )}
          </div>
        </div>
        <div className="agents-kpi-card">
          <div className="agents-kpi-l">CAPABILITY COVERAGE</div>
          <div className="agents-kpi-v" data-testid="kpi-coverage">
            {cov ? `${Math.round((cov.covered / Math.max(1, cov.total)) * 100)}%` : "—"}
          </div>
          <div className="agents-kpi-s">
            {cov
              ? `${cov.covered} of ${cov.total} capabilities covered`
              : "loading"}
          </div>
        </div>
        <div className="agents-kpi-card">
          <div className="agents-kpi-l">INVOCATIONS · 24H</div>
          <div className="agents-kpi-v">{summary.inv24.toLocaleString()}</div>
          <div className="agents-kpi-s">across {summary.total} bindings</div>
        </div>
        <div className="agents-kpi-card">
          <div className="agents-kpi-l">GAPS</div>
          <div className="agents-kpi-v">{cov?.gaps ?? "—"}</div>
          <div className="agents-kpi-s">capabilities without coverage</div>
        </div>
      </div>

      <div className="agents-tabs" role="tablist" data-testid="agents-tabs">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`agents-tab ${tab === t.key ? "active" : ""}`}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "fleet" && (
        <div className="agents-grid" data-testid="fleet-grid">
          {bindingsQ.isLoading ? (
            <div className="agent-card" data-testid="fleet-loading">
              Loading bindings…
            </div>
          ) : bindings.length === 0 ? (
            <div className="agent-card" data-testid="fleet-empty">
              <div className="agent-name">No agents bound yet</div>
              <div className="agent-vendor">
                {manage
                  ? "Bind your first agent to give your plays capability coverage."
                  : "Your CXO has not bound any agents in this workspace."}
              </div>
            </div>
          ) : (
            bindings.map((b) => (
              <AgentCard
                key={b.id}
                binding={b}
                readOnly={!manage}
                onPause={(id) => revoke.mutate(id)}
                onTest={handleTest}
                onRevoke={(id) => revoke.mutate(id)}
                testResult={testResults[b.id] ?? null}
              />
            ))
          )}
        </div>
      )}

      {tab === "coverage" && (
        <div className="agents-coverage-wrap" data-testid="coverage-wrap">
          <div className="agents-cov-desc">
            Capabilities your plays consume. Empty cells show gaps — bind a new
            agent to close them.
          </div>
          {coverageQ.isLoading ? (
            <div data-testid="coverage-loading">Loading capabilities…</div>
          ) : (
            <CoverageMatrix
              rows={coverageQ.data?.capabilities ?? []}
              bindings={bindings}
            />
          )}
        </div>
      )}

      {tab === "telemetry" && (
        <div className="agents-telemetry" data-testid="telemetry-list">
          <div className="agents-tel-h">
            <span className="pulse-dot" />
            Per-binding telemetry · last 24h
          </div>
          {bindings.map((b) => {
            const t = b.telemetry ?? {};
            const inv =
              typeof t["invocations_24h"] === "number"
                ? (t["invocations_24h"] as number)
                : 0;
            const spark = readSparkData(t);
            return (
              <div
                key={b.id}
                className="tel-row tel-ok"
                data-testid={`tel-row-${b.id}`}
              >
                <div className="tel-time">{relativeTime(b.last_invocation_at)}</div>
                <div className="tel-agent">{b.agent_name}</div>
                <div className="tel-action">{inv.toLocaleString()} calls</div>
                <div className="tel-mandate">
                  <MiniSparkline data={spark} color="var(--accent)" />
                </div>
              </div>
            );
          })}
          {bindings.length === 0 && (
            <div data-testid="telemetry-empty" style={{ padding: 12 }}>
              No bindings yet — telemetry will appear here once agents start
              firing.
            </div>
          )}
        </div>
      )}

      <ByoModelPanel workspaceId={workspaceId} manage={manage} />

      <VoiceConfigPanel workspaceId={workspaceId} manage={manage} />

      {bindOpen && (
        <BindModal
          agents={availableQ.data?.agents ?? []}
          onClose={() => setBindOpen(false)}
          onSubmit={handleBindSubmit}
          busy={bind.isPending}
          error={
            bind.error
              ? bind.error.message || "Bind failed"
              : null
          }
        />
      )}
    </div>
  );
}
